import React, { useState } from 'react';
import {
  X,
  Phone,
  Mail,
  MapPin,
  Calendar,
  MessageSquare,
  Clock,
  User,
  CheckCircle,
  CheckSquare,
  XCircle,
  Trash2,
  Volume2,
  FileText,
  Edit2
} from 'lucide-react';
import apiService from './services/api';
import { TranscriptDisplay, TranscriptEditor, CallModal } from './components';

function RequestDetailModal({ interaction, onClose, onUpdate }) {
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozeComment, setSnoozeComment] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);

  if (!interaction) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-gray-100 text-gray-800 border border-gray-300';
      case 'urgent': return 'bg-red-800 text-white border border-red-900';
      case 'existing_client_request': return 'bg-teal-100 text-teal-800 border border-teal-300';
      default: return 'bg-gray-100 text-gray-700 border border-gray-300';
    }
  };

  const getTypeDisplay = (type) => {
    const typeMap = {
      'call': 'Phone Call',
      'email': 'Email',
      'sms': 'SMS',
      'google_lead': 'Google Ads Lead',
      'website_message': 'Website Message',
      'phone_text': 'Phone/Text',
      'google_ads_phone_text': 'Google Ads Phone/Text',
      'google_ads_call': 'Google Ads Call',
      'other': 'Other'
    };
    return typeMap[type] || type || 'Unknown';
  };

  const handleSnooze = async () => {
    if (!snoozeDate) {
      setError('Please select a follow-up date');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await apiService.snoozeInteraction(interaction.id, snoozeDate, snoozeComment);
      setShowSnoozeModal(false);
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to snooze interaction');
    } finally {
      setProcessing(false);
    }
  };


  const handleStatusUpdate = async (newStatus) => {
    const statusMessages = {
      'converted': 'Mark this lead as converted (won)?',
      'resolved': 'Mark this existing client request as resolved?'
    };

    if (!window.confirm(statusMessages[newStatus])) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await apiService.patch(`/amn/interactions/${interaction.id}/`, { status: newStatus });
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkAsLost = async () => {
    setProcessing(true);
    setError(null);

    try {
      await apiService.markInteractionAsLost(interaction.id, lostReason);
      setShowLostModal(false);
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to mark as lost');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this interaction? This action cannot be undone.')) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await apiService.delete(`/amn/interactions/${interaction.id}/`);
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete interaction');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveTranscript = async (transcriptData) => {
    setProcessing(true);
    setError(null);

    try {
      await apiService.updateInteractionTranscript(interaction.id, transcriptData);
      setEditingTranscript(false);
      onUpdate && onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to save transcript');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
        <div className="bg-white border border-gray-200 shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between sticky top-0 z-10 border-b border-red-900">
            <h2 className="text-sm font-semibold">Request Details</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-2 mt-2 bg-red-50 border border-red-200 p-2">
              <p className="text-red-800 text-xs">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="px-3 py-2 space-y-2">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <span className={`px-2 py-0.5 text-xs ${getStatusColor(interaction.status)}`}>
                {interaction.status?.replace('_', ' ') || 'new'}
              </span>
              <span className="text-xs text-gray-500">ID: {interaction.id}</span>
            </div>

            {/* Contact Information */}
            <div className="border border-gray-200 p-2 bg-white">
              <h3 className="text-xs font-medium text-gray-900 mb-2 flex items-center uppercase">
                <User className="w-3.5 h-3.5 mr-1" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div>
                  <label className="text-gray-500">Name</label>
                  <p className="text-gray-900 font-medium">
                    {interaction.client_id ? (
                      <a
                        href={`/clients/${interaction.client_id}`}
                        className="text-red-700 hover:text-red-900 hover:underline"
                        onClick={(e) => { e.preventDefault(); onClose(); window.location.href = `/clients/${interaction.client_id}`; }}
                      >
                        {interaction.person ?
                          `${interaction.person.first_name || ''} ${interaction.person.last_name || ''}`.trim() ||
                          interaction.person.phone_number || 'Unknown'
                          : 'Unknown'}
                      </a>
                    ) : interaction.person?.id ? (
                      <a
                        href={`/leads/${interaction.person.id}`}
                        className="text-blue-700 hover:text-blue-900 hover:underline"
                        onClick={(e) => { e.preventDefault(); onClose(); window.location.href = `/leads/${interaction.person.id}`; }}
                      >
                        {`${interaction.person.first_name || ''} ${interaction.person.last_name || ''}`.trim() ||
                          interaction.person.phone_number || 'Unknown'}
                      </a>
                    ) : (
                      'Unknown'
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-gray-500 flex items-center">
                    <Phone className="w-3 h-3 mr-1" />
                    Phone
                  </label>
                  <p className="text-gray-900">{interaction.person?.phone_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-500 flex items-center">
                    <Mail className="w-3 h-3 mr-1" />
                    Email
                  </label>
                  <p className="text-gray-900">{interaction.person?.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-500 flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    Address
                  </label>
                  <p className="text-gray-900">
                    {interaction.person?.address && interaction.person?.city
                      ? `${interaction.person.address}, ${interaction.person.city} ${interaction.person.zip_code || ''}`
                      : interaction.person?.city || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Interaction Details */}
            <div className="border border-gray-200 p-2">
              <h3 className="text-xs font-medium text-gray-900 mb-2 flex items-center uppercase">
                <MessageSquare className="w-3.5 h-3.5 mr-1" />
                Interaction Details
              </h3>
              <div className="space-y-2 text-xs">
                <div className="grid grid-cols-1 md:grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-gray-500">Type</label>
                    <p className="text-gray-900">{getTypeDisplay(interaction.type)}</p>
                  </div>
                  <div>
                    <label className="text-gray-500 flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Date & Time
                    </label>
                    <p className="text-gray-900">{formatDate(interaction.date_time)}</p>
                  </div>
                </div>

                <div>
                  <label className="text-gray-500">Message / Notes</label>
                  <div className="mt-1 bg-gray-50 border border-gray-200 p-2 text-gray-900 whitespace-pre-wrap">
                    {interaction.content || 'No message content available'}
                  </div>
                </div>

                {/* Snooze Information (if snoozed) */}
                {interaction.status === 'snoozed' && (
                  <div className="bg-gray-50 border border-gray-200 p-2">
                    <div className="flex items-center text-gray-700 font-medium mb-1">
                      <Clock className="w-3 h-3 mr-1" />
                      Snoozed Until
                    </div>
                    <p className="text-gray-900 font-semibold">{formatShortDate(interaction.snooze_until)}</p>
                    {interaction.snooze_comment && (
                      <p className="text-gray-700 mt-1">{interaction.snooze_comment}</p>
                    )}
                  </div>
                )}

                {/* Lost Reason (if lost) */}
                {interaction.status === 'lost' && interaction.lost_reason && (
                  <div className="bg-gray-50 border border-gray-200 p-2">
                    <div className="flex items-center text-gray-700 font-medium mb-1">
                      <XCircle className="w-3 h-3 mr-1" />
                      Lost Reason
                    </div>
                    <p className="text-gray-700">{interaction.lost_reason}</p>
                  </div>
                )}

                {/* Call Recording */}
                {interaction.file_url && (
                  <div>
                    <label className="text-gray-500 flex items-center mb-1">
                      <Volume2 className="w-3 h-3 mr-1" />
                      Call Recording
                    </label>
                    <audio controls className="w-full h-8" style={{ height: '32px' }}>
                      <source src={interaction.file_url} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}

                {/* Call Transcript */}
                {(interaction.transcript || interaction.type === 'call' || interaction.type === 'phone_call' || interaction.type === 'google_ads_phone_call' || interaction.type === 'google_ads_call') && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-gray-500 flex items-center">
                        <FileText className="w-3 h-3 mr-1" />
                        Call Transcript
                      </label>
                      {!editingTranscript && !processing && (
                        <button
                          onClick={() => setEditingTranscript(true)}
                          className="text-xs text-red-800 hover:text-red-900 flex items-center"
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                          {interaction.transcript ? 'Edit' : 'Add'}
                        </button>
                      )}
                    </div>

                    {editingTranscript ? (
                      <TranscriptEditor
                        initialTranscript={interaction.transcript}
                        onSave={handleSaveTranscript}
                        onCancel={() => setEditingTranscript(false)}
                      />
                    ) : interaction.transcript ? (
                      <TranscriptDisplay transcript={interaction.transcript} />
                    ) : (
                      <p className="text-xs text-gray-500 italic">No transcript available</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="text-xs text-gray-500 flex items-center justify-between border-t border-gray-200 pt-2">
              <span>Created: {formatShortDate(interaction.created_at)}</span>
              <span>Updated: {formatShortDate(interaction.updated_at)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-3 py-2 flex flex-wrap gap-1 border-t border-gray-200 sticky bottom-0">
            {interaction.person?.phone_number && (
              <button
                onClick={() => setShowCallModal(true)}
                disabled={processing}
                className="flex items-center px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 hover:border-green-600 hover:text-green-600 transition-colors disabled:opacity-50"
              >
                <Phone className="w-3.5 h-3.5 mr-1" />
                Call
              </button>
            )}
            <button
              onClick={() => setShowSnoozeModal(true)}
              disabled={processing}
              className="flex items-center px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 hover:border-red-800 hover:text-red-800 transition-colors disabled:opacity-50"
            >
              <Clock className="w-3.5 h-3.5 mr-1" />
              Snooze
            </button>

            <button
              onClick={() => handleStatusUpdate('resolved')}
              disabled={processing}
              className="flex items-center px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 hover:border-teal-600 hover:text-teal-600 transition-colors disabled:opacity-50"
            >
              <CheckSquare className="w-3.5 h-3.5 mr-1" />
              Resolve
            </button>
            {interaction.status !== 'existing_client_request' && (
              <>
                <button
                  onClick={() => handleStatusUpdate('converted')}
                  disabled={processing}
                  className="flex items-center px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 hover:border-red-800 hover:text-red-800 transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                  Won
                </button>

                <button
                  onClick={() => setShowLostModal(true)}
                  disabled={processing}
                  className="flex items-center px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 hover:border-orange-600 hover:text-orange-600 transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Lost
                </button>
              </>
            )}

            <button
              onClick={handleDelete}
              disabled={processing}
              className="flex items-center px-2 py-1 text-xs bg-red-800 text-white border border-red-900 hover:bg-red-900 transition-colors disabled:opacity-50 ml-auto"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Snooze Modal */}
      {showSnoozeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-2">
          <div className="bg-white border border-gray-200 shadow-lg max-w-md w-full mx-4">
            <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between border-b border-red-900">
              <h3 className="text-sm font-semibold">Snooze Lead</h3>
              <button
                onClick={() => setShowSnoozeModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Follow-up Date
                </label>
                <input
                  type="date"
                  value={snoozeDate}
                  onChange={(e) => setSnoozeDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-800"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Comment / Reminder (optional)
                </label>
                <textarea
                  value={snoozeComment}
                  onChange={(e) => setSnoozeComment(e.target.value)}
                  rows={3}
                  className="w-full px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-800"
                  placeholder="Add a note..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 p-2 text-red-800 text-xs">
                  {error}
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-3 py-2 flex justify-end gap-1 border-t border-gray-200">
              <button
                onClick={() => setShowSnoozeModal(false)}
                disabled={processing}
                className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSnooze}
                disabled={processing}
                className="px-2 py-1 text-xs bg-red-800 text-white border border-red-900 hover:bg-red-900 transition-colors disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Snooze'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lost Reason Modal */}
      {showLostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-2">
          <div className="bg-white border border-gray-200 shadow-lg max-w-md w-full mx-4">
            <div className="bg-orange-600 text-white px-3 py-2 flex items-center justify-between border-b border-orange-700">
              <h3 className="text-sm font-semibold">Mark as Lost</h3>
              <button
                onClick={() => setShowLostModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  rows={3}
                  className="w-full px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-600"
                  placeholder="e.g. Too far, price, no response..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 p-2 text-red-800 text-xs">
                  {error}
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-3 py-2 flex justify-end gap-1 border-t border-gray-200">
              <button
                onClick={() => setShowLostModal(false)}
                disabled={processing}
                className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsLost}
                disabled={processing}
                className="px-2 py-1 text-xs bg-orange-600 text-white border border-orange-700 hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Mark as Lost'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Modal */}
      <CallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        phoneNumber={interaction?.person?.phone_number}
        personName={interaction?.person ? `${interaction.person.first_name || ''} ${interaction.person.last_name || ''}`.trim() || interaction.person.phone_number : null}
        personId={interaction?.person?.id}
        apiService={apiService}
      />
    </>
  );
}

export default RequestDetailModal;
